"use client";

import React, { useEffect, useState } from "react";

import Input from "@/components/ui/input";

interface NumberInputProps extends Omit<
  React.ComponentPropsWithoutRef<typeof Input>,
  "type" | "value" | "onChange" | "min" | "max"
> {
  value: number;
  onValueChange: (value: number) => void;
  min?: number;
  max?: number;
}

/**
 * Controlled number input that allows the field to be temporarily empty
 * while editing. Commits valid values on every keystroke; on blur, if
 * the field is empty, commits and displays the `min` value (default 0).
 */
const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  ({ value, onValueChange, onBlur, ...props }, ref) => {
    const [display, setDisplay] = useState<number | "">(value);

    const minVal = props.min;
    const maxVal = props.max;

    useEffect(() => {
      setDisplay(value);
    }, [value]);

    return (
      <Input
        ref={ref}
        type="number"
        value={display}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "") {
            setDisplay("");
          } else {
            const num = parseInt(raw, 10);
            if (
              !isNaN(num) &&
              (minVal == undefined || num >= minVal) &&
              (maxVal == undefined || num <= maxVal)
            ) {
              setDisplay(num);
              onValueChange(num);
            }
          }
        }}
        onBlur={(e) => {
          if (display === "") {
            const fallback = minVal ?? 0;
            setDisplay(fallback);
            onValueChange(fallback);
          }
          onBlur?.(e);
        }}
        {...props}
      />
    );
  },
);

NumberInput.displayName = "NumberInput";
export { NumberInput };
